/**
 * Previous / Next controls for the header of an open detail modal
 * (kanban 97abd058). The buttons carry the list position ("3 of 50") and
 * the shortcut hint in their titles; the keys themselves are bound by the
 * modal through hooks/useDetailNavKeys.ts.
 *
 * `actionPrefix` names the recorded action (email_next / email_prev,
 * order_next / order_prev) so the usage graph can tell a Next from a
 * fresh open. The page records the event; the data-action attribute lets
 * the shared Guru recorder dedupe against it.
 */
import type { DetailNavigation } from './detailNavigation';
import styles from './DetailNavButtons.module.css';

interface DetailNavButtonsProps {
  navigation: DetailNavigation;
  /** 'email' or 'order': the record kind the actions are named after. */
  actionPrefix: 'email' | 'order';
  /** Singular noun for the titles ("Next email"). */
  noun: string;
}

const DetailNavButtons: React.FC<DetailNavButtonsProps> = ({ navigation, actionPrefix, noun }) => {
  const { index, count, total, hasPrev, hasNext, loadingMore, goPrev, goNext } = navigation;
  const shown = total != null && total > count ? total : count;
  const position = index >= 0 ? `${index + 1} of ${shown.toLocaleString()}` : null;
  return (
    <div className={styles.wrap} data-testid={`${actionPrefix}-nav`}>
      <button
        type="button"
        className={styles.btn}
        onClick={() => goPrev('button')}
        disabled={!hasPrev}
        title={`Previous ${noun} (k or ←)`}
        aria-label={`Previous ${noun}`}
        data-testid={`${actionPrefix}-nav-prev`}
        data-action={`${actionPrefix}_prev`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Prev
      </button>
      {position && (
        <span className={styles.position} data-testid={`${actionPrefix}-nav-position`}>
          {position}
        </span>
      )}
      <button
        type="button"
        className={styles.btn}
        onClick={() => goNext('button')}
        disabled={!hasNext || loadingMore}
        title={`Next ${noun} (j or →)`}
        aria-label={`Next ${noun}`}
        aria-busy={loadingMore || undefined}
        data-testid={`${actionPrefix}-nav-next`}
        data-action={`${actionPrefix}_next`}
      >
        {loadingMore ? 'Loading…' : 'Next'}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
};

export default DetailNavButtons;
